import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, ChevronLeft } from 'lucide-react'
import { useBuildStore } from '@/store/build-store'
import { useConfigStore } from '@/store/config-store'
import { BuilderLayout } from '@/components/layout/BuilderLayout'
import { lazy, Suspense } from 'react'
const ModelViewer = lazy(() =>
  import('@/components/builder/viewer/ModelViewer').then((m) => ({ default: m.ModelViewer }))
)
import { Button } from '@/components/ui/Button'
import { formatCurrency, calculateTotal } from '@/lib/pricing'
import { runConstraintEngine } from '@/lib/constraint-engine'
import { trackReviewViewed } from '@/lib/tracking'

export default function Review() {
  const navigate = useNavigate()
  const { selectedModelId, selectedOptionIds, setStep, setSnapshotDataUrl } = useBuildStore()
  const { config } = useConfigStore()

  const capturedRef = useRef(false)

  useEffect(() => {
    if (selectedModelId) trackReviewViewed(selectedModelId)
  }, [selectedModelId])

  // Capture the Three.js canvas as a JPEG after it has had time to render
  useEffect(() => {
    if (capturedRef.current) return
    const timeout = setTimeout(() => {
      try {
        const canvas = document.querySelector('canvas')
        if (!canvas) return
        const dataUrl = canvas.toDataURL('image/jpeg', 0.85)
        if (dataUrl && dataUrl !== 'data:,') {
          setSnapshotDataUrl(dataUrl)
          capturedRef.current = true
        }
      } catch {
        // Cross-origin or tainted canvas — proceed without snapshot
      }
    }, 900) // wait for R3F first render
    return () => clearTimeout(timeout)
  }, [setSnapshotDataUrl])

  if (!config || !selectedModelId) {
    navigate('/build/model')
    return null
  }

  const model = config.models.find((m) => m.id === selectedModelId)!
  const selectedOptions = config.options.filter((o) => selectedOptionIds.includes(o.id))
  const total = calculateTotal(selectedModelId, selectedOptionIds, config.models, config.options)
  const upgradeTotal = selectedOptions.reduce((s, o) => s + o.price, 0)
  const disclaimer = config.settings['pricing_disclaimer'] ?? ''

  const constraintResult = runConstraintEngine(selectedModelId, selectedOptionIds, config.constraints)

  const groupedOptions = selectedOptions.reduce<Record<string, typeof selectedOptions>>((acc, opt) => {
    const cat = config.categories.find((c) => c.id === opt.category_id)
    const key = cat?.name ?? 'Upgrades'
    if (!acc[key]) acc[key] = []
    acc[key].push(opt)
    return acc
  }, {})

  const handleContinue = () => {
    setStep('form')
    navigate('/build/submit')
  }

  return (
    <BuilderLayout showSummary={false}>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 mb-1">Review Your Build</h1>
        <p className="text-slate-500">Confirm your selections before requesting a quote.</p>
      </div>

      {/* Preview illustration */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-card mb-6 overflow-hidden p-4">
        <Suspense fallback={<div className="aspect-[4/3] bg-slate-100 animate-pulse rounded-lg" />}>
          <ModelViewer model={model} selectedOptions={selectedOptions} />
        </Suspense>
      </div>

      {constraintResult.blockers.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 space-y-2">
          <div className="flex items-center gap-2 text-red-700 font-semibold text-sm">
            <AlertTriangle className="h-4 w-4" />
            Your build has issues that need to be resolved
          </div>
          {constraintResult.blockers.map((b, i) => (
            <p key={i} className="text-sm text-red-600">{b.message}</p>
          ))}
          <Button variant="secondary" size="sm" onClick={() => navigate('/build/customize')}>
            Fix in Configurator
          </Button>
        </div>
      )}

      {/* Build card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-card mb-6 overflow-hidden">
        <div className="bg-dark-900 px-6 py-4">
          <div className="flex items-baseline justify-between">
            <h2 className="text-white font-bold text-lg">{model.name}</h2>
            <span className="text-brand-400 font-semibold">{formatCurrency(model.base_price)}</span>
          </div>
          <p className="text-slate-400 text-sm mt-0.5">{model.sqft} sq ft · {model.sleeps} sleeps · {model.bathrooms} bath{model.has_loft ? ' · loft' : ''}</p>
        </div>

        <div className="p-6">
          {Object.keys(groupedOptions).length === 0 ? (
            <p className="text-slate-400 text-sm italic mb-4">No upgrades selected.</p>
          ) : (
            <div className="space-y-5 mb-6">
              {Object.entries(groupedOptions).map(([category, opts]) => (
                <div key={category}>
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">{category}</h3>
                  <div className="space-y-2">
                    {opts.map((opt) => (
                      <div key={opt.id} className="flex justify-between items-baseline text-sm">
                        <span className="text-slate-700">{opt.name}</span>
                        <span className="text-slate-600 font-medium ml-4 shrink-0">
                          {opt.price === 0 ? 'Included' : `+${formatCurrency(opt.price)}`}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="border-t border-slate-100 pt-4 space-y-2">
            <div className="flex justify-between text-sm text-slate-500">
              <span>Base Model</span><span>{formatCurrency(model.base_price)}</span>
            </div>
            {upgradeTotal > 0 && (
              <div className="flex justify-between text-sm text-slate-500">
                <span>Upgrades ({selectedOptions.length})</span><span>+{formatCurrency(upgradeTotal)}</span>
              </div>
            )}
            <div className="flex justify-between items-baseline pt-2 border-t border-slate-100">
              <span className="font-bold text-slate-900">Estimated Total</span>
              <span className="text-2xl font-bold text-brand-600">{formatCurrency(total)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      {disclaimer && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-6">
          <p className="text-xs text-slate-500 leading-relaxed">{disclaimer}</p>
        </div>
      )}

      {constraintResult.warnings.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 space-y-1.5">
          <div className="flex items-center gap-2 text-amber-700 font-semibold text-sm mb-1">
            <AlertTriangle className="h-4 w-4" />
            Notes
          </div>
          {constraintResult.warnings.map((w, i) => (
            <p key={i} className="text-sm text-amber-700">{w.message}</p>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between">
        <Button variant="secondary" onClick={() => navigate('/build/customize')}>
          <ChevronLeft className="h-4 w-4" /> Back to Customize
        </Button>
        <Button
          size="lg"
          onClick={handleContinue}
          disabled={constraintResult.blockers.length > 0}
        >
          Request a Quote →
        </Button>
      </div>
    </BuilderLayout>
  )
}
